/**
 * Base edit handler
 * Provides common functionality for all edit handlers
 */

import type { ToolExecutionContext, EditPreview } from '../../types';
import { BaseEditHandler } from '../../types';
import { generateDiff, hasChanges } from '../../utils/diff-generator';
import { generateConfirmationToken } from '../../utils/confirmation-token';
import { withExponentialBackoff } from '../../execution';

/**
 * Edit handler base class
 * Extends the interface-based base with common implementation
 */
export abstract class EditHandlerBase<T = any> extends BaseEditHandler<T> {
  /**
   * Execute with retry logic
   */
  async executeWithRetry(
    params: Record<string, any>,
    context?: ToolExecutionContext
  ): Promise<EditPreview> {
    return withExponentialBackoff(() => this.execute(params, context) as Promise<EditPreview>);
  }

  /**
   * Generate preview with diff and confirmation token
   */
  async generatePreview(current: T, updates: Record<string, any>): Promise<EditPreview> {
    const changes = generateDiff(current as any, updates);

    if (!hasChanges(changes)) {
      throw new Error('No changes detected');
    }

    const token = generateConfirmationToken({
      entityType: this.id.replace('edit_', ''),
      entityId: (current as any).id,
      changes: updates,
      userId: 'system',
      chatId: 'system',
      messageId: 'system',
    });

    return {
      type: 'preview',
      entity: this.id.replace('edit_', ''),
      id: (current as any).id,
      changes,
      confirmationToken: token,
      preview: { ...current, ...updates },
    };
  }
}
